import { Button } from '@cluey/cluey-components';
import classNames from 'classnames';
import moment from 'moment';
import { useCallback, useState } from 'react';

import { api } from '../../../../api';
import Cross from '../../../../assets/images/icon-outline-cross-white.svg';
import { InlineLoader } from '../../../../common/InlineLoader';
import env from '../../../../env';
import { formatError } from '../../../../util/errors';
import { Checkbox } from '../../../../v2/components/ui/checkbox';
import { SUBSCRIPTION_LOCAL_STORAGE_KEYS, SubscriptionLocalStorage } from '../SubscriptionLocalStorage';
import useSubscriptionContext, {
  CANCELLATION_TYPE,
  CONFIRMATION_TYPE,
  SUBSCRIPTION_TYPE,
} from '../useSubscriptionContext';

export const SpecialOffer = () => {
  const {
    clueyPlus: {
      getSubscription,
      showDetailsDialog,
      showConfirmationDialog,
      showCancellationDialog,
      toast: { setShow: setToastShow, setMessage: setToastMessage },
    },
    student: { selectedStudentEnrolments, selectedStudent },
  } = useSubscriptionContext();

  const [bundledCustomerOfferAgreed, setBundledCustomerOfferAgreed] = useState(false);

  const { data: accountDetails } = api.account.details.useQuery();

  const {
    mutate: trialConversion,
    isLoading: isLoadingTrialConversion,
    error: trialConversionError,
  } = api.clueyPlus.trialConversion.useMutation();

  const {
    mutate: applyDiscount,
    isLoading: isLoadingApplyDiscount,
    error: applyDiscountError,
  } = api.clueyPlus.applyDiscount.useMutation();

  const { subscriptionId, subscriptionType, subscriptionPaidUntilDate } = getSubscription() ?? {};

  // TODO: $20 and $30 here still hardcoded
  const bundledCustomerOffer = useCallback(() => {
    return (
      <div className="flex h-full w-full flex-col gap-y-6 rounded-3xl bg-[#01220D] p-6 text-white">
        <div className="flex flex-col gap-y-4">
          <p className="mx-auto rounded-full bg-green-3 px-2 py-1 text-xs font-semibold text-grey-6">
            A special offer for you
          </p>
          <h2 className="merriweather text-center text-[32px] font-bold">Keep your learning momentum going.</h2>
        </div>
        <p className="px-2 text-center text-sm font-normal text-green-1">
          Switch to a Cluey+ subscription today for{' '}
          <span className="font-semibold">
            $20/month <span className="text-blood-orange-4">(33% off)</span> for the first three months
          </span>
          , then $30/month ongoing.
        </p>
        <div className="flex flex-row items-center gap-x-2">
          <Checkbox
            id="special-offer-checkbox"
            checked={bundledCustomerOfferAgreed}
            onCheckedChange={() => setBundledCustomerOfferAgreed(!bundledCustomerOfferAgreed)}
            className="bg-white"
          />
          <label htmlFor="special-offer-checkbox" className="cursor-pointer !text-sm font-medium">
            I agree to ongoing monthly payments for my Cluey+ subscription (cancel anytime)
          </label>
        </div>
        <div className="flex w-full flex-col justify-center gap-y-2">
          {trialConversionError && <p className="text-red-4">{formatError(trialConversionError)}</p>}
          <Button
            size="lg"
            type="submit"
            disabled={!bundledCustomerOfferAgreed}
            className={classNames(
              'flex !h-9 w-full flex-row items-center justify-center !border-blood-orange-4 !bg-blood-orange-4 !font-medium !text-white',
              !bundledCustomerOfferAgreed && 'cursor-not-allowed'
            )}
            onClick={() => {
              trialConversion(
                {
                  accountId: accountDetails?.customerSfid,
                  customerSubscriptionId: subscriptionId ?? '',
                  countryCode: accountDetails?.country,
                  effectiveFromDate: moment().format('YYYY-MM-DD'),
                  discountId:
                    accountDetails?.country === 'NZ'
                      ? env.REACT_APP_CLUEY_PLUS_BUNDLED_CUSTOMER_DISCOUNT_CODE_NZ
                      : env.REACT_APP_CLUEY_PLUS_BUNDLED_CUSTOMER_DISCOUNT_CODE_AU,
                },
                {
                  onSuccess: () => {
                    showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_DOWNGRADE_SUBSCRIPTION);
                    SubscriptionLocalStorage.set(
                      SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_SUBS_BEING_CREATED,
                      selectedStudent.studentId
                    );
                  },
                  onError: (error) => {
                    setToastShow(true);
                    setToastMessage(formatError(error));
                    SubscriptionLocalStorage.remove(
                      SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_SUBS_BEING_CREATED,
                      selectedStudent.studentId
                    );
                  },
                }
              );
            }}
          >
            {isLoadingTrialConversion ? <InlineLoader /> : 'Keep Cluey+ active'}
          </Button>
          <p className="text-center">or Cluey+ will expire on {moment(subscriptionPaidUntilDate).format('DD MMM')}</p>
        </div>
      </div>
    );
  }, [
    accountDetails?.country,
    accountDetails?.customerSfid,
    bundledCustomerOfferAgreed,
    isLoadingTrialConversion,
    selectedStudent.studentId,
    setToastMessage,
    setToastShow,
    showConfirmationDialog,
    subscriptionId,
    subscriptionPaidUntilDate,
    trialConversion,
    trialConversionError,
  ]);

  const handleApplyDiscount = useCallback(() => {
    applyDiscount(
      {
        accountId: accountDetails?.customerSfid,
        customerSubscriptionId: subscriptionId ?? '',
        discountId:
          accountDetails?.country === 'NZ'
            ? env.REACT_APP_CLUEY_PLUS_BUNDLED_CUSTOMER_DISCOUNT_CODE_NZ
            : env.REACT_APP_CLUEY_PLUS_BUNDLED_CUSTOMER_DISCOUNT_CODE_AU,
      },
      {
        onSuccess: () => {
          SubscriptionLocalStorage.set(
            SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_DISCOUNTED_SUBS_BEING_CREATED,
            selectedStudent.studentId
          );
          showConfirmationDialog(CONFIRMATION_TYPE.CLUEY_PLUS_DISCOUNT_APPLIED);
        },
        onError: (error) => {
          setToastShow(true);
          setToastMessage(formatError(error));
        },
      }
    );
  }, [
    accountDetails?.country,
    accountDetails?.customerSfid,
    applyDiscount,
    selectedStudent.studentId,
    setToastMessage,
    setToastShow,
    showConfirmationDialog,
    subscriptionId,
  ]);

  // TODO: $20 and 33% here still hardcoded
  const clueyPlusOnlyOffer = useCallback(() => {
    return (
      <div className="flex h-full w-full flex-col rounded-3xl bg-[#01220D] p-6 text-white">
        <div className="flex flex-row justify-end">
          <button type="button" onClick={() => showDetailsDialog()} className="hover:bg-white/10" aria-label="Close">
            <img src={Cross} className="h-6 w-6" alt="close icon" />
          </button>
        </div>
        <div className="flex flex-col gap-y-6">
          <div className="flex flex-col gap-y-4">
            <p className="mx-auto rounded-full bg-green-3 px-2 py-1 text-xs font-semibold text-grey-6">
              A special offer for you
            </p>
            <h2 className="merriweather text-center text-[32px] font-bold leading-tight">
              Keep your learning momentum going.
            </h2>
          </div>
          <p className="px-2 text-center text-sm font-normal text-green-1">
            Stay on-track for{' '}
            <b>
              $20/month <span className="text-blood-orange-4">(33% off)</span> for your next three months of Cluey+
            </b>
          </p>
          <div className="flex w-full flex-col justify-center gap-y-2">
            {applyDiscountError && <p className="text-red-4">{formatError(applyDiscountError)}</p>}
            <Button
              size="lg"
              type="submit"
              className="flex !h-9 w-full max-w-sm flex-row items-center justify-center !border-blood-orange-4 !bg-blood-orange-4 !text-base !font-medium text-white"
              onClick={handleApplyDiscount}
            >
              {isLoadingApplyDiscount ? <InlineLoader /> : 'Keep my subscription'}
            </Button>
            <Button
              size="sm"
              appearance="reverse"
              className="w-full border-none !bg-transparent !font-medium !text-white hover:!bg-transparent hover:!text-white"
              onClick={() => {
                showCancellationDialog(CANCELLATION_TYPE.CANCEL_CLUEY_PLUS);
              }}
            >
              I still want to cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }, [applyDiscountError, isLoadingApplyDiscount, showCancellationDialog, showDetailsDialog, handleApplyDiscount]);

  switch (subscriptionType) {
    case SUBSCRIPTION_TYPE.BUNDLED:
      return bundledCustomerOffer();
    case SUBSCRIPTION_TYPE.TRIAL:
    case SUBSCRIPTION_TYPE.CLUEY_PLUS:
      // Don't show the special offer for separate billing
      if (selectedStudentEnrolments.length > 0) {
        return null;
      }
      return clueyPlusOnlyOffer();
    default:
      return null;
  }
};
